import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation } from "react-router-dom";
import { StarDecorations } from "components/ui/StarDecorations";
import { StepIndicator } from "components/ui/StepIndicator";
import { ResultCardELBB } from "components/ui/ResultCardELBB";
import { SuccessCardELBB } from "components/ui/SuccessCardELBB";
import { HiDocumentText, HiUsers, HiSearch } from "react-icons/hi";
import characterFormELBB from "assets/img/2026/characterFormELBB.png";
import {
  formDataELBBSchema,
  FormDataELBBType,
} from "schemas/cadastro-elbb.schema";
import { 
  getPerspectivasECBB,
  PerspectivaECBB
} from "services/category.service";
import { findPlayer } from "services/profile.service";
import { findManagers } from "services/entrega.service";
import { useAuth } from "context/AuthContext";
import { IniciativaELBBPayload } from "types/iniciativa-elbb-payload";
import { salvarIniciativaELBB, atualizarIniciativa } from "services/iniciativa-elbb.service";
import { ManagerSelector } from "components/ui/ManagerSelector";

export function CadastroELBB() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [participanteInput, setParticipanteInput] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [perspectivasECBB, setPerspectivasECBB] = useState<PerspectivaECBB[]>([]);
  const [loadingPerspectivas, setLoadingPerspectivas] = useState(true);
  const [perspectivaSelecionada, setPerspectivaSelecionada] = useState<PerspectivaECBB | null>(null);
  const [playersEncontrados, setPlayersEncontrados] = useState<any[]>([]);
  const [buscandoPlayers, setBuscandoPlayers] = useState(false);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [gestores, setGestores] = useState<any[]>([]);
  const [loadingGestores, setLoadingGestores] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editPopulatedRef = useRef(false);

  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
    trigger,
  } = useForm<FormDataELBBType>({
    resolver: zodResolver(formDataELBBSchema),
    defaultValues: {
      TITULO: "",
      CATEGORIA: "",
      INDICADOR_NOME: "",
      inicioEntrega: "",
      conclusaoEntrega: "",
      GESTOR_ATUAL_NOME: "",
      participantes: [],
      DESCRICAO: "",
      RESULTADO: "",
    },
  });

  const formData = watch();
  const categoriaSelecionada = watch("CATEGORIA");

  // Identificar modo de edição
  useEffect(() => {
    const editData = location.state?.editData;
    if (editData) {
      setIsEditMode(true);
      setEditingId(editData._id);
    }
  }, [location.state]);

  // Preencher dados de edição após perspectivas serem carregadas (executa apenas uma vez)
  useEffect(() => {
    const editData = location.state?.editData;
    if (editData && !editPopulatedRef.current && !loadingPerspectivas && perspectivasECBB.length > 0) {
      editPopulatedRef.current = true;
      console.log("Dados para edição:", editData);
      
      // Preencher campos básicos
      setValue("TITULO", editData.title || "");
      
      // Preencher categoria/perspectiva
      if (editData.indicator?.perspectiva) {
        setValue("CATEGORIA", editData.indicator.perspectiva);
      }
      
      // Preencher direcionador estratégico (após categoria)
      if (editData.indicator?.nome) {
        setTimeout(() => {
          setValue("INDICADOR_NOME", editData.indicator.nome);
        }, 100);
      }
      
      // Formatar datas para o input (YYYY-MM-DD)
      let startDate = "";
      let endDate = "";
      
      if (editData.start) {
        if (editData.start.$date) {
          startDate = new Date(editData.start.$date).toISOString().split('T')[0];
        } else if (typeof editData.start === 'string') {
          startDate = new Date(editData.start).toISOString().split('T')[0];
        } else if (typeof editData.start === 'number') {
          startDate = new Date(editData.start).toISOString().split('T')[0];
        }
      }
      
      if (editData.end) {
        if (editData.end.$date) {
          endDate = new Date(editData.end.$date).toISOString().split('T')[0];
        } else if (typeof editData.end === 'string') {
          endDate = new Date(editData.end).toISOString().split('T')[0];
        } else if (typeof editData.end === 'number') {
          endDate = new Date(editData.end).toISOString().split('T')[0];
        }
      }
      
      console.log("Datas formatadas - Início:", startDate, "Fim:", endDate);
      
      if (startDate) {
        setValue("inicioEntrega", startDate);
      }
      if (endDate) {
        setValue("conclusaoEntrega", endDate);
      }
      
      // Preencher descrição e resultado
      setValue("DESCRICAO", editData.description || "");
      setValue("RESULTADO", editData.result || "");

      // Preencher gestor
      const managerDetail = editData.original_manager_detail;
      const nomeGestor = managerDetail?.nome_guerra ||
                        managerDetail?.nome ||
                        editData.original_manager;
      if (nomeGestor) {
        setValue("GESTOR_ATUAL_NOME", nomeGestor);
        if (managerDetail) {
          setGestores([managerDetail]);
        }
      }

      // Preencher participantes
      if (editData.team && Array.isArray(editData.team)) {
        const participantes = editData.team.map((member: any) => ({
          _id: member._id || member.code,
          name: member.name,
          extra: {
            chave: member.code,
            nome: member.name,
          }
        }));
        setValue("participantes", participantes);
      }
    }
  }, [location.state, setValue, loadingPerspectivas, perspectivasECBB]);

  useEffect(() => {
    const carregarGestorDoUsuario = async () => {
      // Em modo de edição, o gestor vem dos dados do editData — não sobrescrever
      if (location.state?.editData) return;

      if (!user?.detail?.gestor) {
        return;
      }
      setLoadingGestores(true);
      try {
        const gestoresData = await findManagers("", [1, 2, 3]);
        if (gestoresData) {
          const gestorDoUsuario = gestoresData.find(
            (g: any) => g._id === user.detail.gestor
          );
          if (gestorDoUsuario) {
            setGestores([gestorDoUsuario]);
            const nomeGestor =
              gestorDoUsuario.nome_guerra ||
              gestorDoUsuario.nome ||
              gestorDoUsuario.name ||
              gestorDoUsuario.extra?.nome_guerra ||
              gestorDoUsuario.extra?.nome ||
              gestorDoUsuario._id;
            setValue("GESTOR_ATUAL_NOME", nomeGestor);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar gestor:", error);
      } finally {
        setLoadingGestores(false);
      }
    };

    carregarGestorDoUsuario();
  }, [user, setValue, location.state]);

  // Adicionar owner como primeiro participante no modo criação
  useEffect(() => {
    if (!user || isEditMode) return;
    const ownerParticipante = {
      _id: user._id,
      name: user.name || user.extra?.nome,
      extra: {
        chave: user.extra?.chave,
        nome: user.name || user.extra?.nome,
        funcao: user.extra?.funcao_localizacao,
      },
    };
    setValue("participantes", [ownerParticipante]);
  }, [user, isEditMode, setValue]);

  useEffect(() => {
    const carregarPerspectivas = async () => {
      setLoadingPerspectivas(true);
      try {
        const perspectivas = await getPerspectivasECBB();
        setPerspectivasECBB(perspectivas);
      } catch (error) {
        console.error('Erro ao carregar perspectivas:', error);
      } finally {
        setLoadingPerspectivas(false);
      }
    };

    carregarPerspectivas();
  }, []);

  useEffect(() => {
    if (categoriaSelecionada) {
      const perspectiva = perspectivasECBB.find(p => p.title === categoriaSelecionada);
      setPerspectivaSelecionada(perspectiva || null);
      // Limpar seleção de direcionador ao mudar perspectiva
      setValue("INDICADOR_NOME", "");
    } else {
      setPerspectivaSelecionada(null);
      setValue("INDICADOR_NOME", "");
    }
  }, [categoriaSelecionada, perspectivasECBB, setValue]);

  const buscarPlayers = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setPlayersEncontrados([]);
      setMostrarResultados(false);
      return;
    }

    try {
      setBuscandoPlayers(true);
      const resultado = await findPlayer(searchTerm);
      const participantesAtuais = getValues("participantes") || [];
      const filtrado = (resultado || []).filter((p: any) => {
        // const isManager = p.is_manager || p.detail?.is_manager;
        const jaAdicionado = participantesAtuais.some((sel: any) => sel._id === p._id);
        return !jaAdicionado;
      });
      setPlayersEncontrados(filtrado);
      setMostrarResultados(true);
    } catch (error) {
      console.error('Erro ao buscar players:', error);
      setPlayersEncontrados([]);
    } finally {
      setBuscandoPlayers(false);
    }
  };

  const adicionarParticipante = (player: any) => {
    const participantes = formData.participantes || [];
    const jaAdicionado = participantes.some(p => p._id === player._id);
    const naoOwners = participantes.filter(p => p._id !== user?._id).length;

    if (!jaAdicionado && naoOwners < 4) {
      setValue("participantes", [...participantes, player]);
      setParticipanteInput("");
      setPlayersEncontrados([]);
      setMostrarResultados(false);
    }
  };

  const removerParticipante = (index: number) => {
    const participantes = formData.participantes || [];
    setValue(
      "participantes",
      participantes.filter((_, i) => i !== index)
    );
  };

  const nextStep = async () => {
    let fieldsToValidate: (keyof FormDataELBBType)[] = [];

    if (currentStep === 1) {
      fieldsToValidate = [
        "TITULO",
        "CATEGORIA",
        "INDICADOR_NOME",
        "inicioEntrega",
        "conclusaoEntrega",
        "GESTOR_ATUAL_NOME",
      ];
    } else if (currentStep === 2) {
      fieldsToValidate = ["participantes"];
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid && currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep === 1 && isEditMode) {
      navigate("/listagem");
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const mapFormDataToPayload = (data: FormDataELBBType): IniciativaELBBPayload => {
    const direcionadorCod = (data.INDICADOR_NOME && perspectivaSelecionada?.strategicDrivers?.[data.INDICADOR_NOME]) || "";

    const gestorSelecionado = gestores.find((g) => {
      const nomeGestor =
        g.nome_guerra || g.nome || g.name || g.extra?.nome_guerra || g.extra?.nome || g._id;
      return nomeGestor === data.GESTOR_ATUAL_NOME;
    });
    
    const payload: any = {
      title: data.TITULO,
      description: data.DESCRICAO,
      result: data.RESULTADO || "",
      start: { $date: new Date(data.inicioEntrega).toISOString() },
      end: { $date: new Date(data.conclusaoEntrega).toISOString() },
      indicator: {
        nome: data.INDICADOR_NOME,
        cod: direcionadorCod,
        perspectiva: data.CATEGORIA,
      },
      created: { $date: new Date().toISOString() },
      aproved: { $date: new Date().toISOString() },
      categoryPremio: "ELBB",
      // Dados do usuário logado
      owner: user?.detail?.chave || user?._id,
      prefixo: user?.detail?.prefixo,
      diretoria: user?.detail?.diretoria,
      owner_cad_missao: user?.detail?.cad_missao,
      owner_cod_etapa: user?.detail?.cod_etapa,
      owner_funcao_localizacao: user?.detail?.funcao_localizacao,
      original_manager: gestorSelecionado?.chave || data.GESTOR_ATUAL_NOME,
      // Participantes
      team: data.participantes.map((player) => ({
        _id: player._id,
        name: player.name || player.extra?.nome,
        chave: player._id,
      })),

      status: "aguardando",
      preRegistration: true,
    };

    // Adicionar _id quando estiver editando
    if (isEditMode && editingId) {
      payload._id = editingId;
    }

    return payload;
  };

  if (showSuccess) {
    return (
      <SuccessCardELBB
        onClose={() => navigate("/listagem")}
      />
    );
  }

  if (showResult) {
    const formDataWithDefaults = {
      ...formData,
      INDICADOR_NOME: formData.INDICADOR_NOME || "",
    };
    return (
      <ResultCardELBB
        formData={formDataWithDefaults}
        onBack={() => setShowResult(false)}
        isSubmitting={isSubmitting}
        onConfirm={async () => {
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
            const payload = mapFormDataToPayload(formData);
            console.log("Payload para envio:", payload);
            
            let response;
            if (isEditMode && editingId) {
              // Modo de edição - usar PUT
              response = await atualizarIniciativa(payload);
            } else {
              // Modo de criação - usar POST
              response = await salvarIniciativaELBB(payload);
            }
            
            // Verificar se a resposta contém mensagem de erro
            if (response?.message) {
              alert(response.message);
              return;
            }
            
            setShowResult(false);
            setShowSuccess(true);
          } catch (error: any) {
            console.error("Erro ao salvar iniciativa:", error);
            
            // Verificar se o erro contém mensagem específica
            const mensagemErro = error?.response?.data?.message || 
                                error?.message || 
                                "Erro ao salvar a iniciativa. Tente novamente.";
            
            alert(mensagemErro);
          } finally {
            setIsSubmitting(false);
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-x-hidden relative">
      {/* Star Decorations - Hidden on Mobile */}
      <div aria-hidden="true" className="hidden md:block pointer-events-none">
        <StarDecorations
          count={18}
          density="medium"
          includeEmojis={true}
          includeCrosses={false}
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-start container px-4 sm:px-6 my-4 sm:my-6 lg:my-8 mx-auto">
        <h1 className="text-green-bb-2 text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3">
          {isEditMode ? "Editar iniciativa" : "Cadastrar iniciativa"}
        </h1>

        {/* Form Card */}
        <div className="bg-white-bb rounded-xl sm:rounded-2xl p-4 sm:p-6 md:px-8 lg:px-12 xl:px-20 py-6 sm:py-8 w-full shadow-xl sm:shadow-2xl relative">
          <StepIndicator
            currentStep={currentStep}
            steps={[
              { icon: HiDocumentText as any, number: 1, iconColor: "text-pink-bb-2", inactiveIconColor: "text-gray-200" },
              { icon: HiUsers as any, number: 2, iconColor: "text-pink-bb-2", inactiveIconColor: "text-gray-200" },
              { icon: HiSearch as any, number: 3, iconColor: "text-pink-bb-2", inactiveIconColor: "text-gray-200" },
            ]}
          />

          {/* Step Content */}
          {currentStep === 1 && (
            <div className="relative">
              <div className="flex flex-col lg:flex-row items-start lg:justify-between mb-6 sm:mb-7 lg:mb-8 relative">
                <div className="w-full lg:max-w-lg">
                  <h2 className="text-xl sm:text-2xl md:text-2xl lg:text-4xl text-green-bb-2 font-bold mb-4 sm:mb-6 lg:mb-8">
                    Inicie a inscrição da sua{" "}
                    <br className="hidden sm:block" />
                    iniciativa no{" "}
                    <span className="text-blue-bb-1">Prêmio ELBB</span>
                  </h2>

                  <h2 className="text-lg sm:text-xl md:text-xl lg:text-xl font-bold text-gray-bb-1 mb-4">
                    Informações da Iniciativa:
                  </h2>
                </div>
                <img
                  src={characterFormELBB}
                  alt="Personagem"
                  className="hidden lg:block absolute right-0 top-0 h-auto z-10"
                  style={{ transform: "translateY(-25px)" }}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-bb-1 mb-1.5 sm:mb-2">
                    Nome da Iniciativa
                  </label>
                  <input
                    type="text"
                    {...register("TITULO")}
                    aria-label="Nome da Iniciativa"
                    placeholder="250 caracteres"
                    className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
                      errors.TITULO ? "border-red-500" : "border-gray-bb-4"
                    }`}
                  />
                  {errors.TITULO && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.TITULO.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-bb-1 mb-1.5 sm:mb-2">
                    Perspectiva ECBB
                  </label>
                  <select
                    {...register("CATEGORIA")}
                    aria-label="Perspectiva ECBB"
                    disabled={loadingPerspectivas}
                    className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
                      errors.CATEGORIA ? "border-red-500" : "border-gray-bb-4"
                    } ${
                      loadingPerspectivas ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <option value="">
                      {loadingPerspectivas ? "Carregando..." : "Selecione..."}
                    </option>
                    {perspectivasECBB.map((perspectiva) => (
                      <option key={perspectiva._id} value={perspectiva.title}>
                        {perspectiva.title}
                      </option>
                    ))}
                  </select>
                  {errors.CATEGORIA && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.CATEGORIA.message}
                    </p>
                  )}
                </div>

                {/* CAMPO OCULTO - Direcionadores Estratégicos (desativado temporariamente, pode ser reativado) */}
                <div className="hidden">
                  <label className="block text-sm font-medium text-gray-bb-1 mb-1.5 sm:mb-2">
                    Direcionadores Estratégicos{" "}
                  </label>
                  <select
                    {...register("INDICADOR_NOME")}
                    disabled={!perspectivaSelecionada}
                    className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
                      errors.INDICADOR_NOME
                        ? "border-red-500"
                        : "border-gray-bb-4"
                    } ${
                      !perspectivaSelecionada
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <option value="">
                      {!perspectivaSelecionada
                        ? "Selecione primeiro a Perspectiva ECBB"
                        : "Selecione..."}
                    </option>
                    {perspectivaSelecionada?.strategicDrivers &&
                      Object.entries(perspectivaSelecionada.strategicDrivers).map(
                        ([key, value]) => (
                          <option key={key} value={value}>
                            {key}. {value}
                          </option>
                        )
                      )}
                  </select>
                  {errors.INDICADOR_NOME && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">
                      {errors.INDICADOR_NOME.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-bb-1 mb-1.5 sm:mb-2">
                      Início da entrega
                    </label>
                    <input
                      type="date"
                      {...register("inicioEntrega")}
                      aria-label="Início da entrega"
                      min="2025-01-01"
                      max="2025-12-31"
                      className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
                        errors.inicioEntrega
                          ? "border-red-500"
                          : "border-gray-bb-4"
                      }`}
                    />
                    {errors.inicioEntrega && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">
                        {errors.inicioEntrega.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-bb-1 mb-1.5 sm:mb-2">
                      Conclusão da entrega
                    </label>
                    <input
                      type="date"
                      {...register("conclusaoEntrega")}
                      aria-label="Conclusão da entrega"
                      min="2025-01-01"
                      max="2025-12-31"
                      className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all ${
                        errors.conclusaoEntrega
                          ? "border-red-500"
                          : "border-gray-bb-4"
                      }`}
                    />
                    {errors.conclusaoEntrega && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">
                        {errors.conclusaoEntrega.message}
                      </p>
                    )}
                  </div>
                </div>

                <ManagerSelector
                  managers={gestores}
                  loading={loadingGestores}
                  error={errors.GESTOR_ATUAL_NOME}
                  value={formData.GESTOR_ATUAL_NOME}
                  onChange={(val) => setValue("GESTOR_ATUAL_NOME", val, { shouldValidate: true })}
                  onSearch={async (search) => {
                    setLoadingGestores(true);
                    try {
                      const result = await findManagers(search, [1, 2, 3]);
                      setGestores(result || []);
                    } catch (err) {
                      console.error("Erro ao buscar gestores:", err);
                    } finally {
                      setLoadingGestores(false);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-bb-1 mb-3 sm:mb-4">
                Informações da equipe:
              </h2>
              <p className="text-gray-bb-2 text-sm sm:text-base mb-4 sm:mb-6">
                Inclua a equipe (mínimo 2, máximo 5 participantes incluindo você) que efetivamente atuou
                com você na conclusão da entrega.
              </p>

              <div className="mb-5 sm:mb-6">
                {(() => {
                  const naoOwners = (formData.participantes || []).filter(
                    (p: any) => p._id !== user?._id
                  ).length;
                  const isAtMax = naoOwners >= 4;
                  return (
                    <>
                      <div className="flex gap-2 relative">
                        <input
                          type="text"
                          value={participanteInput}
                          aria-label="Buscar participante por nome"
                          disabled={isAtMax}
                          onChange={(e) => {
                            setParticipanteInput(e.target.value);
                            buscarPlayers(e.target.value);
                          }}
                          placeholder={isAtMax ? "Limite máximo de 5 participantes atingido" : "Digite o nome do funcionário"}
                          className="flex-1 border border-gray-bb-4 rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-bb-2"
                        />
                        {buscandoPlayers && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-bb"></div>
                          </div>
                        )}
                      </div>

                      {isAtMax && (
                        <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                          Limite máximo de 5 participantes atingido (você + 4 colaboradores). Remova um para adicionar outro.
                        </p>
                      )}
                    </>
                  );
                })()}

                {/* Resultados da busca */}
                {mostrarResultados && playersEncontrados.length > 0 && (
                  <div className="mt-2 border border-gray-bb-4 rounded-lg max-h-60 overflow-y-auto bg-white shadow-lg">
                    {playersEncontrados.map((player, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => adicionarParticipante(player)}
                        className="w-full text-left p-2.5 sm:p-3 hover:bg-blue-50 active:bg-blue-100 border-b border-gray-bb-4 last:border-b-0 transition-colors touch-manipulation"
                      >
                        <div className="font-medium text-gray-bb-1 text-sm sm:text-base">
                          {player.extra?.nome_guerra || player.name || player._id}
                          {player._id && (
                            <span className="text-xs sm:text-sm text-gray-bb-2 ml-2">
                              ({player._id})
                            </span>
                          )}
                        </div>
                        {player.extra?.funcao && (
                          <div className="text-xs sm:text-sm text-gray-bb-2">
                            {player.extra.funcao}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {mostrarResultados && playersEncontrados.length === 0 && !buscandoPlayers && (
                  <div className="mt-2 p-2.5 sm:p-3 text-gray-bb-2 text-xs sm:text-sm bg-gray-50 rounded-lg">
                    Nenhum funcionário encontrado
                  </div>
                )}
              </div>

              {formData.participantes.length > 0 && (
                <div className="mb-5 sm:mb-6">
                  <h3 className="font-medium text-gray-bb-1 text-sm sm:text-base mb-2 sm:mb-3">
                    Participantes adicionados:
                  </h3>
                  <div className="space-y-2">
                    {formData.participantes.map((participante, index) => {
                      const isOwner = participante._id === user?._id;
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-bb-3 p-2.5 sm:p-3 rounded-lg"
                        >
                          <div className="flex-1 min-w-0 pr-2">
                            <span className="text-gray-bb-1 font-medium text-sm sm:text-base break-words">
                              {participante.extra?.nome_guerra || participante.name || participante._id}
                            </span>
                            {isOwner && (
                              <span className="text-xs text-blue-bb font-semibold ml-1">(você)</span>
                            )}
                            {(participante.extra?.chave || participante._id) && (
                              <span className="text-xs sm:text-sm text-gray-bb-2 ml-1 sm:ml-2">
                                ({participante.extra?.chave || participante._id})
                              </span>
                            )}
                            {participante.extra?.funcao && (
                              <span className="text-xs sm:text-sm text-gray-bb-2 ml-1 block sm:inline">
                                - {participante.extra.funcao}
                              </span>
                            )}
                          </div>
                          {!isOwner && (
                            <button
                              type="button"
                              onClick={() => removerParticipante(index)}
                              className="text-red-500 hover:text-red-700 active:text-red-800 text-lg sm:text-xl p-1 flex-shrink-0 touch-manipulation"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-gray-bb-2 text-sm sm:text-base mb-4 sm:mb-6">
                <strong>Total de participantes:</strong>{" "}
                {formData.participantes.length}
              </p>
              {errors.participantes && (
                <p className="text-red-500 text-xs sm:text-sm mt-1">
                  {errors.participantes.message}
                </p>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-bb-1 mb-3 sm:mb-4">
                Informações detalhadas:
              </h2>

              <div className="mb-5 sm:mb-6">
                <label className="block text-sm font-medium text-gray-bb-1 mb-1.5 sm:mb-2">
                  Descrição da Iniciativa
                </label>
                <textarea
                  {...register("DESCRICAO")}
                  aria-label="Descrição da Iniciativa"
                  rows={3}
                  maxLength={500}
                  className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent resize-none transition-all ${
                    errors.DESCRICAO ? "border-red-500" : "border-gray-bb-4"
                  }`}
                  placeholder="Descreva brevemente a iniciativa..."
                />
                <div className="text-right text-xs sm:text-sm text-gray-bb-2 mt-1">
                  {formData.DESCRICAO?.length || 0}/500
                </div>
                {errors.DESCRICAO && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">
                    {errors.DESCRICAO.message}
                  </p>
                )}
              </div>

              <p className="text-gray-bb-2 text-sm sm:text-base mb-4 sm:mb-6 font-bold">
                Por que essa iniciativa foi determinante para o cumprimento da perspectiva da ECBB à qual está vinculada e quais resultados foram obtidos?
              </p>

              <textarea
                {...register("RESULTADO")}
                aria-label="Por que essa iniciativa foi determinante para o cumprimento da perspectiva da ECBB e quais resultados foram obtidos"
                rows={6}
                className={`w-full border rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent resize-none transition-all ${
                  errors.RESULTADO ? "border-red-500" : "border-gray-bb-4"
                }`}
                placeholder="Descreva detalhadamente os resultados obtidos..."
              />

              <div className="text-right text-xs sm:text-sm text-gray-bb-2 mt-2">
                Caracteres: {formData.RESULTADO?.length || 0}/2000
              </div>
              {errors.RESULTADO && (
                <p className="text-red-500 text-xs sm:text-sm mt-1">
                  {errors.RESULTADO.message}
                </p>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-6 sm:mt-8">
            <button
              onClick={prevStep}
              disabled={currentStep === 1 && !isEditMode}
              className={`w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-bold text-sm sm:text-base transition-all touch-manipulation ${
                currentStep === 1 && !isEditMode
                  ? "bg-gray-bb-4 text-gray-bb-2 cursor-not-allowed"
                  : "bg-gray-bb-1 text-white-bb hover:bg-gray-bb-2 active:scale-95"
              }`}
            >
              VOLTAR
            </button>

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="w-full sm:w-auto bg-yellow-bb text-blue-bb-3 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-bold text-sm sm:text-base hover:opacity-90 active:scale-95 transition-all touch-manipulation"
              >
                CONTINUAR
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  console.log("Botão FINALIZAR clicado");
                  setShowResult(true);
                }}
                className="w-full sm:w-auto bg-yellow-bb text-blue-bb-3 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-bold text-sm sm:text-base hover:opacity-90 active:scale-95 transition-all touch-manipulation"
              >
                FINALIZAR
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
